import { Typography } from "@material-ui/core";
import React from "react";
import { formatAsIndianCurrency } from "../../../utils/currency";

const colorSchemes = [
  {
    id: "blueGradient",
    strokeId: "strokeId1",
    stroke1: "#B6A259",
    stroke2: "#FA861A",
    color1: "#57E754",
    color2: "#3ecc3f",
    circleFill: "#35BE52",
  },
  {
    id: "greenGradient",
    strokeId: "strokeId2",
    stroke: "#369EE6",
    color1: "#149CF5 ",
    color2: "#149cf5",
    circleFill: "#369EE6",
  },
  {
    id: "orangeGradient",
    strokeId: "strokeId3",
    stroke: "#FA861A",
    color1: "#f2c94c",
    color2: "#FAA71A",
    circleFill: "#FA861A",
  },
];

export interface BarChartProps {
  bars: Array<{ value: number; label: string }>;
  barSpacing?: number;
  height?: number;
  width?: number;
  showLabels?: boolean;
  className?: string;
  preserveAspectRatio?: string | undefined;
  labelWidth?: number;
  handleBarChartClick: (schemeType: string) => void;
}
function BarChart({
  bars,
  handleBarChartClick,
  width = 500,
  height = 300,
  barSpacing = 70,
  showLabels = true,
  className = "",
  preserveAspectRatio = undefined,
  labelWidth = 0,
}: BarChartProps) {
  const totalValue = bars
    .map((bar) => bar.value)
    .reduce((sum, current) => sum + current, 0);

  const barHeight = (height - barSpacing * (bars.length + 1)) / bars.length;
  const x = showLabels ? -labelWidth : 0;
  return (
    <>
      <svg
        aria-describedby="bar-chart-description"
        aria-labelledby="bar-chart-title"
        className={`bar-chart ${className}`}
        height={height}
        preserveAspectRatio={preserveAspectRatio}
        role="img"
        width={width}
        version="1.1"
        viewBox={`${x} 0 ${width} ${height}`}
      >
        {!!bars.length &&
          bars.map((bar: any, i: number) => {
            const barY = (barHeight + barSpacing) * i + barSpacing;
            const textY = barY + barHeight / 2;
            const value = bars[i].value;
            const formattedValue = formatAsIndianCurrency(value);
            const valueInBar = bar.value > x / 2;
            const barLength =
              10 +
              (Number(totalValue)
                ? (bar.value / Number(totalValue)) * 100 * 2
                : 0);
            return (
              <>
                <text
                  textAnchor={"middle"}
                  x={100 + i * 150}
                  y={300 - barLength - 55}
                  className={"scheme-type-label"}
                >
                  {bar.label} -{" "}
                  {Math.round(
                    100 *
                    (Number(totalValue) ? bar.value / Number(totalValue) : 0)
                  )}
                  %
                </text>
                <text
                  textAnchor={"middle"}
                  x={100 + i * 150}
                  y={300 - barLength - 22}
                  className={"scheme-type-value"}
                >
                  {formattedValue}
                </text>
                <defs>
                  <linearGradient
                    id={colorSchemes[i].id}
                    x1="0%"
                    y1="100%"
                    x2="0%"
                    y2="0%"
                  >
                    <stop
                      offset="0%"
                      stop-color={colorSchemes[i].color1}
                      stop-opacity="0"
                    />
                    <stop
                      offset="100%"
                      stop-color={colorSchemes[i].color2}
                      stop-opacity="1"
                    />
                  </linearGradient>
                  <linearGradient
                    id={colorSchemes[i].strokeId}
                    x1="0%"
                    y1="100%"
                    x2="0%"
                    y2="0%"
                  >
                    <stop
                      offset="0%"
                      stop-color={colorSchemes[i].stroke1}
                      stop-opacity="0"
                    />
                    <stop
                      offset="100%"
                      stop-color={colorSchemes[i].stroke2}
                      stop-opacity="1"
                    />
                  </linearGradient>
                  <clipPath id={`round-corner${i}`}>
                    <rect
                      x={50 + i * 150}
                      y={300 - barLength}
                      width="102"
                      height={barLength + 20}
                      rx="20"
                      ry="20"
                    />
                  </clipPath>
                </defs>
                <rect
                  x={50 + i * 150}
                  y={300 - barLength}
                  width="102"
                  height={barLength}
                  fill={`url(#${colorSchemes[i].id})`}
                  onClick={() => handleBarChartClick(bar.label)}
                  style={{ cursor: 'pointer'}}
                  // rx="20"
                  strokeWidth={4}
                  clipPath={`url(#round-corner${[i]})`}
                  stroke={`url(#${colorSchemes[i].id})`}
                ></rect>
                <circle
                  cx={100 + i * 150}
                  style={{ cursor: 'pointer'}}
                  cy={300 - barLength}
                  onClick={() => handleBarChartClick(bar.label)}
                  r="5"
                  stroke="white"
                  fill={colorSchemes[i].circleFill}
                  strokeWidth={4}
                />
              </>
            );
          })}
      </svg>
    </>
  );
}

export default BarChart;
